import React, { useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useT } from '../../i18n/useT';
import { F } from '../../styles/theme';
import { useCurrency } from '../../context/CurrencyContext';
import { summarizeLots } from '../../utils/taxLots';
import { lotFreeMs, isLotFree, daysUntilFree } from '../../utils/holdingPeriod';
import { fmt, fmtC } from '../../utils/format';
import { Icon, Icons } from './Icons';
import Private from '../Private';

// Layout-Konstanten für die geteilte Zeitachse
const LABEL_W = 116;
const GAP = 12;
const CD_W = 48;

function fmtDate(d, locale = 'de-DE') {
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: '2-digit' });
}

// ── Reife-Monitor mit geteilter Zeitachse ────────────────────────────
// Eyecatcher fürs Schlüssel-Feature: großes Prozent + eine gemeinsame Zeitachse,
// auf der jede Tranche von Kauf → 1-Jahres-Marke läuft, mit vertikaler
// „Heute"-Linie quer über alle. Bewusst flach — keine Glows, keine getönten
// Container (siehe feedback_no_pills_borders_glow.md). Solid-Bars + Mono-Zahlen.
export default function TaxTimeline({ sym, lots, today = new Date() }) {
  const { T } = useTheme();
  const { t } = useT();
  const { locale } = useCurrency();
  const summary = useMemo(() => summarizeLots(lots, today), [lots, today]);
  const todayMs = +today;

  if (!lots.length) {
    return (
      <div style={{ padding: '24px 18px', textAlign: 'center', color: T.sub, fontSize: 13 }}>
        {t('red_tl_no_open_tranches', { sym })}
      </div>
    );
  }

  const freePct = summary.total > 0 ? (summary.free / summary.total) * 100 : 0;
  const freePctR = Math.round(freePct);
  const allFree = freePctR >= 100;

  // Gemeinsame Zeitachse: frühester Kauf → spätestes Frei-Datum (bzw. heute).
  // Frei-Datum = Jahrestag + 1 Tag (holdingPeriod.js), nicht „rollend 365".
  const earliestMs = Math.min(...lots.map(l => +new Date(l.buyDate)));
  const latestFreeMs = Math.max(...lots.map(l => lotFreeMs(l.buyDate) ?? +new Date(l.buyDate)));
  const endMs = Math.max(latestFreeMs, todayMs);
  const totalSpan = endMs - earliestMs || 1;
  const todayPct = Math.max(0, Math.min(100, ((todayMs - earliestMs) / totalSpan) * 100));

  // Tranchen chronologisch (frühester Kauf oben).
  const sortedLots = [...lots].sort((a, b) => +new Date(a.buyDate) - +new Date(b.buyDate));

  // Heute-Linie liegt über der Track-Region (zwischen Label-Spalte und Countdown).
  const heuteLeft = `calc(${LABEL_W + GAP}px + (100% - ${LABEL_W + GAP + GAP + CD_W}px) * ${todayPct / 100})`;

  return (
    <div style={{ padding: '6px 18px 0' }}>
      {/* Hero — großes Prozent über der Frist */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 8 }}>
        <span style={{
          fontFamily: F.display, fontSize: 54, fontWeight: 800,
          letterSpacing: '-0.05em', lineHeight: 0.86,
          color: freePctR > 0 ? T.green : T.text,
        }}>
          {freePctR}<span style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.03em' }}> %</span>
        </span>
        <span style={{
          fontSize: 13, color: T.sub, fontWeight: 500,
          paddingBottom: 8, letterSpacing: '-0.01em', lineHeight: 1.35,
        }}>
          {t('red_tl_hero_l1', { sym })}<br />{t('red_tl_hero_l2')}
        </span>
      </div>

      {/* Nächste Freigabe — eine ruhige Zeile */}
      {!allFree && summary.daysToNextFree !== null && summary.nextFreeLot && (
        <div style={{ fontSize: 12.5, color: T.sub, lineHeight: 1.5, marginBottom: 16 }}>
          {t('red_tl_next_pre')} <span style={{ fontFamily: F.num || F.mono, color: T.text, fontWeight: 600 }}>{t('red_tl_next_days', { days: summary.daysToNextFree })}</span> {t('red_tl_next_mid')}{' '}
          <span style={{ fontFamily: F.num || F.mono, color: T.text, fontWeight: 600 }}><Private>{fmtC(summary.nextFreeLot.amount)}</Private> {sym}</span> {t('red_tl_next_post')}
        </div>
      )}

      {/* Geteilte Zeitachse mit Heute-Marker */}
      <div style={{ position: 'relative', paddingTop: 24 }}>
        {/* Vertikale „Heute"-Linie — startet UNTER dem Label, läuft nie hindurch. */}
        <div style={{ position: 'absolute', top: 18, bottom: 22, left: heuteLeft, width: 1, background: T.sub, opacity: 0.5, zIndex: 2, pointerEvents: 'none' }} />
        {/* Label sitzt über der Linie, mit BG-Hinterlegung als Sicherheitsabstand. */}
        <div style={{
          position: 'absolute', top: 0, left: heuteLeft, transform: 'translateX(-50%)',
          fontFamily: F.mono, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: T.sub, zIndex: 3, whiteSpace: 'nowrap',
          background: T.bg, padding: '0 5px',
        }}>
          {t('red_tl_today')}
        </div>

        {/* Tranchen-Zeilen */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {sortedLots.map((lot, i) => {
            const buyMs = +new Date(lot.buyDate);
            const freeMs = lotFreeMs(lot.buyDate) ?? buyMs;
            const isFree = isLotFree(lot.buyDate, today);
            const fullStartPct = ((buyMs - earliestMs) / totalSpan) * 100;
            const fullEndPct = ((freeMs - earliestMs) / totalSpan) * 100;
            const heldEndPct = ((Math.min(todayMs, freeMs) - earliestMs) / totalSpan) * 100;
            const fullWidthPct = Math.max(0, fullEndPct - fullStartPct);
            const heldWidthPct = Math.max(0, heldEndPct - fullStartPct);
            const daysRemaining = daysUntilFree(lot.buyDate, today) ?? 0;

            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: GAP, padding: '7px 0' }}>
                {/* Label: Menge + Datum */}
                <div style={{ width: LABEL_W, flexShrink: 0 }}>
                  <div style={{ fontFamily: F.num || F.mono, fontSize: 12.5, fontWeight: 600, color: T.text, whiteSpace: 'nowrap' }}>
                    <Private>{fmtC(lot.amount)}</Private>&nbsp;<span style={{ color: T.sub, fontWeight: 500 }}>{sym}</span>
                  </div>
                  <div style={{ fontFamily: F.num || F.mono, fontSize: 9.5, color: T.mute, letterSpacing: '0.03em', marginTop: 1 }}>
                    {fmtDate(lot.buyDate, locale)}
                  </div>
                </div>

                {/* Track auf gemeinsamer Achse */}
                <div style={{ flex: 1, height: 12, position: 'relative', minWidth: 0 }}>
                  {/* voller 365-Tage-Span (Outline) */}
                  <div style={{
                    position: 'absolute', top: 3, left: `${fullStartPct}%`, width: `${fullWidthPct}%`,
                    height: 6, borderRadius: 3,
                    border: `1px solid ${isFree ? T.green : T.line}`,
                  }} />
                  {/* gehaltener Anteil (Solid, kein Glow) */}
                  <div style={{
                    position: 'absolute', top: 3, left: `${fullStartPct}%`, width: `${heldWidthPct}%`,
                    height: 6, borderRadius: 3,
                    background: isFree ? T.green : T.loss,
                  }} />
                  {/* 1-Jahres-Marke (Tick) wenn noch gebunden */}
                  {!isFree && (
                    <div style={{
                      position: 'absolute', top: 0, left: `${fullEndPct}%`,
                      width: 2, height: 12, background: T.green, borderRadius: 1,
                    }} />
                  )}
                </div>

                {/* Countdown / Status */}
                <div style={{ width: CD_W, flexShrink: 0, textAlign: 'right' }}>
                  {isFree ? (
                    <Icon d={Icons.check} size={14} stroke={T.green} sw={3} style={{ display: 'inline-block' }} />
                  ) : (
                    <span style={{ fontFamily: F.num || F.mono, fontSize: 13, fontWeight: 700, color: T.loss }}>
                      {daysRemaining}<span style={{ fontSize: 9, marginLeft: 1, fontWeight: 600, color: T.sub }}>{t('days_short')}</span>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Achsen-Beschriftung (unter der Track-Region) */}
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          paddingLeft: LABEL_W + GAP, paddingRight: CD_W + GAP,
          marginTop: 6, paddingTop: 6, borderTop: `1px solid ${T.lineSoft || T.line}`,
        }}>
          <span style={{ fontFamily: F.num || F.mono, fontSize: 9.5, color: T.mute }}>{fmtDate(new Date(earliestMs), locale)}</span>
          <span style={{ fontFamily: F.num || F.mono, fontSize: 9.5, color: T.mute }}>{fmtDate(new Date(endMs), locale)}</span>
        </div>
      </div>

      {/* Legende — frei / gebunden, Dots statt Pills */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 12, fontSize: 11.5, color: T.sub, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.green, flexShrink: 0 }} />
          <span style={{ fontFamily: F.num || F.mono, color: T.text, fontWeight: 600 }}><Private>{fmt(summary.free, locale)}</Private> {sym}</span>&nbsp;{t('tax_free_legend')}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.loss, flexShrink: 0 }} />
          <span style={{ fontFamily: F.num || F.mono, color: T.text, fontWeight: 600 }}><Private>{fmt(summary.taxable, locale)}</Private> {sym}</span>&nbsp;{t('red_tl_bound')}
        </span>
      </div>
    </div>
  );
}
